import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, ListChecks } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SLUG = 'octopus-vente';
const QUIZ_ROUTE = '/formation/quiz-octopus-vente';

export const useOctopusQuizGate = (enabled) => {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const shownOnce = useRef(false);
  const location = useLocation();
  const navigate = useNavigate();
  const locked = !!status?.blocked;

  const refresh = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/quizzes/${SLUG}/status`);
      setStatus(data);
      return data;
    } catch (e) {
      return null;
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    refresh().then((d) => {
      if (d?.blocked && !shownOnce.current) { shownOnce.current = true; setOpen(true); }
    });
  }, [enabled, refresh]);

  useEffect(() => {
    if (enabled && locked && location.pathname !== QUIZ_ROUTE) refresh();
  }, [location.pathname, enabled, locked, refresh]);

  useEffect(() => {
    if (locked && location.pathname.startsWith('/contracts')) {
      navigate('/dashboard', { replace: true });
      setOpen(true);
    }
  }, [locked, location.pathname, navigate]);

  const goQuiz = () => { setOpen(false); navigate(QUIZ_ROUTE); };

  const popup = (
    <Dialog open={open && locked} onOpenChange={setOpen}>
      <DialogContent className="max-w-md" data-testid="quiz-gate-popup">
        <DialogHeader>
          <div className="mx-auto size-14 rounded-full bg-[#FF48D8]/15 flex items-center justify-center mb-2">
            <Lock className="size-7 text-[#FF48D8]" />
          </div>
          <DialogTitle className="text-center">Quiz obligatoire avant d’accéder aux contrats</DialogTitle>
          <DialogDescription className="text-center">
            Pour débloquer l’onglet <strong>Contrats Octopus</strong>, passez et réussissez le quiz
            « {status?.title || 'Ma vente Octopus de A à Z'} » ({status?.pass_score ?? 17}/20 minimum).
            L’onglet se débloque automatiquement dès votre réussite.
          </DialogDescription>
        </DialogHeader>
        <Button onClick={goQuiz} className="w-full bg-[#FF48D8] hover:bg-[#e63ec2] text-[#100030] font-semibold" data-testid="quiz-gate-go-btn">
          <ListChecks className="size-4 mr-2" /> Passer le quiz maintenant
        </Button>
        <Button variant="ghost" onClick={() => setOpen(false)} className="w-full" data-testid="quiz-gate-later-btn">Plus tard</Button>
      </DialogContent>
    </Dialog>
  );

  return { locked, openPopup: () => setOpen(true), popup, refresh };
};
